<template>
  <section
    :aria-label="
      t('common.addParam', { param: t('carrierPortal.spotMarket.bids') })
    "
    class="spot-market-bid-edit"
  >
    <header>
      <RateQuoteCarrier
        :rate-quote="internalValue"
        class="align-items-center"
      />

      <KeyValuePair
        :label="
          t('common.totalParam', {
            param: t('carrierPortal.spotMarket.bids'),
          })
        "
        :value="
          renderMoney(totalRate, {
            currencyCode: internalValue.currencyCode,
          })
        "
        juicy
      >
        <template #value="{ value }">
          <output
            id="total-rate"
            name="total-rate"
            for="spot-market-bid-linehaul spot-market-bid-fuel spot-market-bid-accessorials"
            form="spot-market-bid"
          >
            {{ value }}
          </output>
        </template>

        <template #label="{ label }">
          <label for="total-rate" class="text-gray-darker">
            {{ label }}
          </label>
        </template>
      </KeyValuePair>
    </header>

    <form id="spot-market-bid" :aria-label="t('carrierPortal.spotMarket.bids')">
      <h2 class="section-header">
        {{
          t('common.paramAmounts', {
            param: t('carrierPortal.spotMarket.bids'),
          })
        }}
      </h2>

      <p v-if="spotMarket.data?.requireFuel" class="require-fuel-header">
        {{ t('carrierPortal.spotMarket.requireFuelHeader') }}
      </p>

      <div class="spot-market-bid-form-row">
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="spot-market-bid-linehaul"
          :label="t('shipments.financials.linehaul')"
          :errors="v$.rawLineHaulNetCharge.$errors"
          class="w-4"
        >
          <InputNumber
            :model-value="internalValue.rawLineHaulNetCharge"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :input-id="inputId"
            :currency="internalValue.currencyCode ?? Currency.USD"
            mode="currency"
            :min="0"
            :currency-display="currencyDisplayMode"
            :disabled="disabled"
            use-grouping
            :placeholder="t('shipments.financials.linehaul')"
            @update:model-value="onUpdateLinehaul"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId, inputClasses, invalid }"
          input-id="spot-market-bid-fuel"
          :label="t('shipments.financials.fuel')"
          class="w-4"
          :optional="!spotMarket.data?.requireFuel"
          :errors="v$.fuelSurchargeAmount.$errors"
        >
          <InputNumber
            v-model="internalValue.fuelSurchargeAmount"
            v-bind="ariaProps"
            :input-id="inputId"
            :class="inputClasses"
            :invalid="invalid"
            :currency="internalValue.currencyCode ?? Currency.USD"
            mode="currency"
            :min="0"
            :currency-display="currencyDisplayMode"
            :disabled="disabled"
            use-grouping
            :placeholder="t('shipments.financials.fuel')"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="spot-market-bid-currency"
          :label="t('currency.currency')"
          class="w-4"
        >
          <CurrencyDropdown
            :id="inputId"
            v-model="internalValue.currencyCode"
            v-bind="ariaProps"
            :disabled="disabled"
            append-to="body"
          />
        </InputWrapper>
      </div>

      <div class="spot-market-bid-form-row accessorial-row">
        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="spot-market-bid-accessorials"
          :label="
            t('common.additionalParam', { param: t('shipments.services', 2) })
          "
          class="w-full"
          optional
        >
          <SpotMarketRateQuoteAccessorials
            :id="inputId"
            v-model="internalValue.accessorials"
            v-bind="ariaProps"
            :available-accessorials="accessorialCodes.data"
            :disabled="disabled"
            class="field"
          />
        </InputWrapper>
      </div>

      <h2 class="section-header">
        {{
          t('common.paramDetails', {
            param: t('carrierPortal.spotMarket.bids'),
          })
        }}
      </h2>

      <div class="spot-market-bid-form-row">
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="spot-market-bid-mode"
          :label="t('shipments.modes')"
          :errors="v$.mode.$errors"
          class="w-4"
        >
          <RygenDropdown
            :id="inputId"
            v-model="internalValue.mode"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :loading="carrierSetting.loading"
            :options="availableModeOptions"
            :placeholder="t('shipments.modes')"
            :disabled="disabled"
            append-to="body"
            option-label="label"
            option-value="value"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="spot-market-bid-equipment-types"
          :label="
            t('common.paramTypes', { param: t('referenceData.equipment') })
          "
          :errors="v$.equipmentType.$errors"
          class="w-4"
        >
          <RygenDropdown
            :id="inputId"
            v-model="internalValue.equipmentType"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :loading="carrierSetting.loading"
            :options="availableEquipmentTypes"
            :placeholder="
              t('common.paramTypes', { param: t('referenceData.equipment') })
            "
            :disabled="disabled"
            option-label="name"
            data-key="id"
            append-to="body"
            filter
            show-clear
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="spot-market-bid-service-level"
          :errors="v$.serviceType.$errors"
          :label="t('carriers.serviceLevels')"
          class="w-4"
        >
          <RygenDropdown
            :id="inputId"
            v-model="internalValue.serviceType"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :loading="carrierSetting.loading"
            :options="availableServiceLevels"
            :placeholder="t('carriers.serviceLevels')"
            :disabled="disabled"
            append-to="body"
          />
        </InputWrapper>
      </div>

      <div class="spot-market-bid-form-row">
        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="spot-market-bid-quote-id"
          :label="t('common.paramId', { param: t('rateShop.quotes') })"
          class="w-4"
          optional
        >
          <InputText
            :id="inputId"
            v-model="internalValue.carrierQuoteNumber"
            v-bind="ariaProps"
            :placeholder="t('common.paramId', { param: t('rateShop.quotes') })"
            :disabled="disabled"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="spot-market-bid-transit-days"
          :label="t('rateShop.transitDays')"
          class="w-4"
          optional
        >
          <InputNumber
            v-model="internalValue.transitTime"
            v-bind="ariaProps"
            :input-id="inputId"
            :placeholder="t('rateShop.transitDays')"
            :disabled="disabled"
          />
        </InputWrapper>
      </div>
    </form>

    <div class="buttons flex flex-gap-1 justify-content-end">
      <RygenButton
        mode="primary"
        icon="pi pi-save"
        :label="t('carrierPortal.spotMarket.placeBid')"
        :disabled="disabled"
        :loading="isSaving || spotMarket.loading"
        @click="onSave"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, ref, toRef, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  required,
  requiredIf,
  serverValidationPlaceholder,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { renderMoney } from '@rygen/rygen-ui/i18n';
import {
  AccessorialCode,
  CarrierSetting,
  Currency,
  DropdownOption,
  EquipmentType,
  Loadable,
  Mode,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import {
  RateQuote,
  RateQuoteAccessorial,
  SpotMarket,
  WellKnownService,
} from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import SpotMarketRateQuoteAccessorials from '@/components/carrierPortal/rateShop/SpotMarketRateQuoteAccessorials.vue';
import RateQuoteCarrier from '@/components/rateShop/RateQuoteCarrier.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import CurrencyDropdown from '@rygen/rygen-ui/components/form/CurrencyDropdown.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';

export type BidPayload = {
  bid: RateQuote;
  onSuccessCallback: () => void;
  onErrorCallback: () => void;
};

const props = defineProps({
  spotMarket: {
    type: Object as PropType<Loadable<SpotMarket>>,
    default: () => ({ loading: false }),
  },
  carrierSetting: {
    type: Object as PropType<Loadable<CarrierSetting>>,
    default: () => ({ loading: false }),
  },
  accessorialCodes: {
    type: Object as PropType<Loadable<AccessorialCode[]>>,
    default: () => ({ loading: false }),
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { spotMarket, carrierSetting, accessorialCodes, disabled } =
  toRefs(props);
const emit = defineEmits<{
  (event: 'save', payload: BidPayload): void;
}>();

const { t } = useI18n();
const { parentEntity } = useParentEntityStore();
const { uid } = useUid('spot-market-rate-shop');

const isSaving = ref(false);
const carrier = toRef(() => spotMarket.value.data?.carrier);
const internalValue: Ref<RateQuote> = ref(
  new RateQuote({
    carrier: carrier.value,
    spotMarketId: spotMarket.value.data?.id,
  }),
);

const rules = {
  rawLineHaulNetCharge: {
    required: required(t('shipments.financials.linehaul')),
  },
  fuelSurchargeAmount: {
    'spotMarketBidEdit.fuelRequired': requiredIf(
      () => spotMarket.value.data?.requireFuel,
      t('shipments.financials.fuel'),
    ),
  },
  mode: {
    required: required(t('shipments.modes')),
  },
  serviceType: {
    required: required(t('carriers.serviceLevels')),
  },
  equipmentType: { serverValidationPlaceholder },
  effectiveDate: { serverValidationPlaceholder },
  expirationDate: { serverValidationPlaceholder },
  origin: { serverValidationPlaceholder },
  destination: { serverValidationPlaceholder },
};

const { externalResults, v$, validate } = useValidations(
  internalValue,
  rules,
  uid,
);

const availableModes: Ref<Mode[]> = computed(() => {
  let carrierSettingModes = carrierSetting.value.data?.modes || [];
  return carrierSettingModes.length > 0
    ? carrierSettingModes
    : Object.values(Mode);
});

const availableModeOptions: Ref<DropdownOption<Mode>[]> = computed(() => {
  return availableModes.value.map((mode) => ({
    label: t(`enumMap.Mode.${mode}`),
    value: mode,
  }));
});

const availableServiceLevels: Ref<string[]> = computed(() => {
  let carrierServiceLevels =
    carrierSetting.value?.data?.serviceLevels
      ?.map((x) => x.name)
      ?.filter(notNull) || [];
  return carrierServiceLevels.length > 0
    ? carrierServiceLevels
    : [WellKnownService.STANDARD];
});

const availableEquipmentTypes: Ref<EquipmentType[]> = computed(() => {
  return carrierSetting.value.data?.equipmentTypes || [];
});

const currencyDisplayMode = computed(() => {
  return internalValue.value.currencyCode === Currency.USD ? 'symbol' : 'code';
});

const totalAccessorialCharges = computed(() => {
  return internalValue.value.accessorials
    .map((a) => a.charge)
    .filter(notNull)
    .reduce((total, curr) => total + curr, 0);
});

const totalRate = computed(() => {
  const linehaul = internalValue.value.rawLineHaulGrossCharge ?? 0;
  const fuel = internalValue.value.fuelSurchargeAmount ?? 0;
  return linehaul + fuel + totalAccessorialCharges.value;
});

const populateAccessorials = () => {
  const topLevelAccessorials =
    parentEntity.value?.requestedAccessorials?.map(
      (code) => new RateQuoteAccessorial({ code }),
    ) ?? [];
  const stopLevelAccessorials = (parentEntity.value?.stops ?? [])
    .map((s) => {
      const stopSequenceNumber = s.sequenceNumber;
      return s.requestedAccessorials.map(
        (code) => new RateQuoteAccessorial({ code, stopSequenceNumber }),
      );
    })
    .flat();

  internalValue.value.accessorials = [
    ...topLevelAccessorials,
    ...stopLevelAccessorials,
  ];

  if (!internalValue.value.id) {
    return;
  }

  internalValue.value.accessorials.forEach((a) => {
    const rateCharge = internalValue.value.rawCharges.find(
      (c) =>
        c.code === a.code?.naturalId &&
        c.stopSequenceNumber === a.stopSequenceNumber,
    );
    a.charge = rateCharge?.charge;
  });
};

const onUpdateLinehaul = (lineHaulNetCharge: number) => {
  internalValue.value.rawLineHaulGrossCharge = lineHaulNetCharge;
  internalValue.value.rawLineHaulNetCharge = lineHaulNetCharge;
};

const validateTotal = () => {
  if (totalRate.value > 0) {
    return true;
  }

  externalResults.value = {
    ...externalResults.value,
    rawLineHaulNetCharge: [t('validations.spotMarketBidEdit.positiveRate')],
  };

  return false;
};

const validateManualConditions = (): boolean => {
  // Don't chain these calls, `one && two`, so a failure in one doesn't prevent
  // successive validations from running
  const isTotalValid = validateTotal();
  return isTotalValid;
};

const onSave = async () => {
  const areAutoValidationsValid = await validate();
  const areManualValidationsValid = validateManualConditions();
  if (areAutoValidationsValid && areManualValidationsValid) {
    isSaving.value = true;
    emit('save', {
      bid: internalValue.value,
      onSuccessCallback: () => (isSaving.value = false),
      onErrorCallback: () => (isSaving.value = false),
    });
  }
};

watch(carrierSetting, (newVal: Loadable<CarrierSetting>) => {
  if (newVal.data?.defaultCurrency) {
    internalValue.value.currencyCode = newVal.data.defaultCurrency;
  }
});

watch(
  () => spotMarket.value.data,
  (newVal) => {
    if (!newVal) {
      return;
    }

    internalValue.value = new RateQuote({
      carrier: newVal.carrier,
      mode: newVal.mode,
      equipmentType: newVal.equipmentType,
      serviceType: newVal.serviceType?.name,
      spotMarketId: newVal.id,
    });

    populateAccessorials();
  },
  { deep: true, immediate: true },
);
</script>

<style>
.spot-market-bid-edit {
  display: flex;
  flex-flow: column;
  gap: var(--sp-3);

  header {
    display: flex;
    justify-content: space-between;
    gap: var(--sp-3);
  }

  #spot-market-bid {
    display: flex;
    flex-flow: column;
    gap: var(--sp-3);

    .require-fuel-header {
      margin-bottom: var(--sp-3);
      color: var(--p-gray-700);
    }

    .spot-market-bid-form-row {
      display: flex;
      gap: var(--sp-3);

      &.accessorial-row {
        margin-top: var(--sp-3);

        .carrier-portal-rate-quote-accessorials {
          margin-top: var(--sp-1);
        }
      }
    }
  }

  .buttons {
    margin-top: var(--sp-6);
  }
}
</style>
